// File formats each attachment category accepts. Used by the Attachment tab,
// the comment box and the author-query panel. "otherAttachment" accepts any
// file.
export const ALLOWED_ATTACHMENT_EXTENSIONS = {
  imageAttachment: ["jpg", "jpeg", "png", "tif", "svg", "eps", "fits", "pdf"],
  mediaAttachment: ["mp4", "gif", "sml", "mp3", "ttml", "vtt", "txt"],
  textAttachment: [
    "pdf", "csv", "txt", "raw", "tar", "zip", "gz", "kml", "kmz",
    "doc", "docx", "rtf", "xls", "xlsx", "ppt", "pps", "pptx", "ppsx",
    "new", "nwk", "xml", "fig", "obj", "ply", "mtl", "mol", "mol2",
    "pdb", "pse", "fits", "fit", "fts", "root", "nii", "u3d", "m",
    "mat", "nb", "ma", "mb", "nbp", "cdf", "ici", "r",
  ],
};

// Category dropdown options, with a short explanation of each.
export const ATTACHMENT_CATEGORY_OPTIONS = [
  { value: "imageAttachment", label: "Image – Figures and images used in the article" },
  { value: "textAttachment", label: "Text – Documents, data and other supporting files" },
  { value: "mediaAttachment", label: "Media – Video, audio, GIF and caption files" },
  { value: "otherAttachment", label: "Other – Anything that doesn't fit above" },
];

// Without alt text, only plain "Image" and "Text" options are offered.
const BASIC_ATTACHMENT_CATEGORY_OPTIONS = [
  { value: "imageAttachment", label: "Image" },
  { value: "textAttachment", label: "Text" },
];

export const getAttachmentCategoryOptions = (alttextEnabled) =>
  alttextEnabled ? ATTACHMENT_CATEGORY_OPTIONS : BASIC_ATTACHMENT_CATEGORY_OPTIONS;

const CATEGORY_LABELS = {
  imageAttachment: "Image",
  mediaAttachment: "Media",
  textAttachment: "Text",
};

export const getFileExtension = (filename) =>
  String(filename || "").includes(".")
    ? String(filename).split(".").pop().toLowerCase()
    : "";

export const isValidAttachmentFile = (attachType, filename) => {
  const allowed = ALLOWED_ATTACHMENT_EXTENSIONS[attachType];
  return !allowed || allowed.includes(getFileExtension(filename));
};

// A media file attached to replace an existing one is uploaded under that
// file's name, keeping the attached file's own extension:
//   "mmc1_updated.mp4"     replacing "mmc1.mp4"     -> "mmc1.mp4"
//   "mmc1-ad_updated.ttml" replacing "mmc1-ad.ttml" -> "mmc1-ad.ttml"
// Returns the attached file's own name when there's nothing to replace.
export const getReplacementFileName = (attachedName, replaceName) => {
  const target = String(replaceName || "").trim();
  if (!target || target === "null") return attachedName;
  const base = target.includes(".") ? target.slice(0, target.lastIndexOf(".")) : target;
  const ext = getFileExtension(attachedName);
  return ext ? `${base}.${ext}` : base;
};

// The File to upload for a media replacement — a renamed copy when needed.
export const toReplacementFile = (file, replaceName) => {
  const name = getReplacementFileName(file.name, replaceName);
  return name === file.name
    ? file
    : new File([file], name, { type: file.type, lastModified: file.lastModified });
};

// The error to show when `filename` can't be attached as `attachType`, or
// null when it can. Formats are only enforced when alt text is enabled.
export const getAttachmentFormatError = (attachType, filename, alttextEnabled) => {
  if (!alttextEnabled || isValidAttachmentFile(attachType, filename)) return null;
  const allowed = ALLOWED_ATTACHMENT_EXTENSIONS[attachType];
  return `"${filename}" is not a valid ${CATEGORY_LABELS[attachType]} file. Allowed formats: ${allowed
    .map((ext) => "." + ext)
    .join(", ")}`;
};
